/**
 * Centralized Grid Breakpoint Patterns
 * 
 * This file contains standardized responsive grid patterns used throughout the application.
 * Using these patterns ensures consistent responsive behavior and better maintainability.
 * 
 * Usage: import { GRID_BREAKPOINTS } from '@/config/grid-breakpoints'
 * Example: <div className={`grid ${GRID_BREAKPOINTS.form} gap-4`}>
 */

export const GRID_BREAKPOINTS = {
  /**
   * Form layouts - Most common pattern (20+ uses)
   * Used for: Forms, settings, basic two-column layouts
   */
  form: 'grid-cols-1 md:grid-cols-2',

  /**
   * Split layouts - Common pattern (8+ uses)  
   * Used for: Dashboard sections, analytics, two-panel views
   */
  split: 'grid-cols-1 lg:grid-cols-2',

  /**
   * Single column - Simple pattern (10+ uses)
   * Used for: Vertical lists, single-column forms, mobile-first content
   */
  single: 'grid-cols-1',

  /**
   * Standard content grid - File Storage pattern (2+ uses)
   * Used for: Universal Storage, content libraries, file galleries
   */
  standard: 'grid-cols-2 sm:grid-cols-2 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4',
  
  /**
   * Document/Page grid - Mobile-optimized single column (like PageGrid)
   * Used for: Document generators, page grids, content that needs full width on mobile
   */
  documents: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4',

  /**
   * Card layouts - Large screen optimization (6+ uses)
   * Used for: Research cards, app library, content that needs more space
   */
  cards: 'grid-cols-1 2xl:grid-cols-2',

  /**
   * Dense content grid - AI Assistants pattern (DEPRECATED - use imageCards)
   * @deprecated Use imageCards instead for consistent visual content layout
   */
  dense: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5',

  /**
   * Image cards - Unified pattern for visual content (6+ uses)
   * Used for: AI Agents, Image Generator, Content Generator, visual galleries
   * Optimized for card-based content with preview images
   */
  imageCards: 'grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4',

  /**
   * Triple column - Dashboard layouts
   * Used for: Three-section layouts, some settings pages
   */
  triple: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',

  /**
   * Token/subscription layouts
   * Used for: Token top-up, subscription features
   */
  subscription: 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3',

  // === BILLING PAGE SPECIFIC PATTERNS ===
  
  /**
   * Billing page main container - Single column layout
   * Used for: Main content wrapper on billing page
   */
  billingMain: 'grid-cols-1',

  /**
   * Billing page section split - Desktop side-by-side layout
   * Used for: Current Plan + Credit Transactions cards
   */
  billingSectionSplit: 'grid-cols-1 lg:grid-cols-1 2xl:grid-cols-2',

  /**
   * Billing page error cards - Tablet+ two-column layout  
   * Used for: Error state subscription + token cards
   */
  billingErrorCards: 'grid-cols-1 md:grid-cols-1 lg:grid-cols-2',

  /**
   * Billing page usage split - Desktop side-by-side with stretch
   * Used for: AI Usage card + Overview cards section
   */
  billingUsageSplit: 'grid-cols-1 lg:grid-cols-1 2xl:grid-cols-2',

  /**
   * Billing page feature breakdown - Tablet+ two-column layout
   * Used for: Feature breakdown grid inside current plan card
   */
  billingFeatureBreakdown: 'grid-cols-1 md:grid-cols-2',

  // === APP LIBRARY SPECIFIC PATTERNS ===
  
  /**
   * App Library pinned apps row - Ultra-wide 4-column layout
   * Used for: Pinned apps DND section (positions 1-3)
   */
  appLibraryPinnedAppsRow: 'grid-cols-1',
} as const;

/**
 * Type for grid breakpoint keys
 */
export type GridBreakpointKey = keyof typeof GRID_BREAKPOINTS;

/**
 * Helper function to get grid classes with custom gap
 */
export const getGridClasses = (
  pattern: GridBreakpointKey, 
  gap: string = 'gap-4'
): string => {
  return `grid ${GRID_BREAKPOINTS[pattern]} ${gap}`;
};

/**
 * Common gap patterns for consistency
 */
export const GRID_GAPS = {
  tight: 'gap-1',
  small: 'gap-2', 
  medium: 'gap-4',
  large: 'gap-6',
  xlarge: 'gap-8',
} as const; 